import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { ProgressBar } from 'primereact/progressbar';
import { InputSwitch } from 'primereact/inputswitch';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import axiosInstance from '../../config/axios.config';
import { getProcessingStatusConfig } from '../../services/leadProcessingService';
import { toast } from 'react-toastify';
import LeadDetailsDialog from './LeadDetailsDialog';

/**
 * Tree Leads Modal (Unified for MLM and OrgChart)
 * Displays leads with tree-based access control
 * Supports: usermlm, orgposition, orguser access types
 *
 * @param {boolean} visible - Modal visibility state
 * @param {function} onHide - Modal close handler
 * @param {number|null} userId - Target user ID (for usermlm/orguser)
 * @param {number|null} positionId - Target position ID (for orgposition)
 * @param {string} userName - Target user/position name for display
 * @param {string} leadType - Lead type filter: 'my', 'network', or 'total'
 * @param {string} accessType - Access type: 'usermlm' | 'orgposition' | 'orguser'
 * @param {string} filterType - For orgposition: 'whole_branch' | 'this_position_only'
 */
const MLMUserLeadsModal = ({
    visible,
    onHide,
    userId,
    positionId = null,
    userName,
    leadType = 'total',
    accessType = 'usermlm',
    filterType = 'whole_branch'
}) => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);

    // Lead details dialog state
    const [showLeadDetails, setShowLeadDetails] = useState(false);
    const [selectedLeadId, setSelectedLeadId] = useState(null);

    // Network inclusion toggle (for usermlm/orguser)
    const [includeNetwork, setIncludeNetwork] = useState(true);

    // Position filter toggle (for orgposition): false = whole_branch, true = this_position_only
    const [thisPositionOnly, setThisPositionOnly] = useState(false);

    // Pagination
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [searchFromDate, setSearchFromDate] = useState(null);
    const [searchToDate, setSearchToDate] = useState(null);
    const [selectedProductType, setSelectedProductType] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState(null);

    // Sorting
    const [sortField, setSortField] = useState('created_at');
    const [sortOrder, setSortOrder] = useState(-1);

    // Product type options
    const productTypeOptions = [
        { label: 'All Products', value: null },
        { label: 'New Personal Loan', value: 'new_personal_loan' },
        { label: 'Personal Loan Top Up', value: 'personal_loan_top_up' },
        { label: 'Personal Loan BT', value: 'personal_loan_balance_transfer' },
        { label: 'Personal Loan BT + Top Up', value: 'personal_loan_bt_top_up' },
        { label: 'New Home Loan', value: 'new_home_loan' },
        { label: 'Home Loan Top Up', value: 'home_loan_top_up' },
        { label: 'Home Loan BT', value: 'home_loan_balance_transfer' },
        { label: 'Home Loan BT + Top Up', value: 'home_loan_bt_top_up' },
        { label: 'Loan Against Property', value: 'loan_against_property' },
        { label: 'LAP Top Up', value: 'lap_top_up' },
        { label: 'LAP BT', value: 'lap_balance_transfer' },
        { label: 'Car Loan', value: 'car_loan' },
        { label: 'Educational Loan', value: 'educational_loan' },
        { label: 'Equipment Loan', value: 'equipment_loan' }
    ];

    // Status options
    const statusOptions = [
        { label: 'All Status', value: null },
        { label: 'New Lead', value: 'new_lead' },
        { label: 'Offers Generated', value: 'offers_generated' },
        { label: 'Broadcasted', value: 'broadcasted' },
        { label: 'Processing', value: 'processing' },
        { label: 'Bank Submit', value: 'bank_submit' },
        { label: 'Bank Queries', value: 'bank_queries' },
        { label: 'Bank Processing', value: 'bank_processing' },
        { label: 'Sanction', value: 'sanction' },
        { label: 'Disbursal', value: 'disbursal' },
        { label: 'Bank Rejected', value: 'bank_rejected' },
        { label: 'Customer Rejected', value: 'customer_rejected' },
        { label: 'Processor Rejected', value: 'processor_rejected' },
        { label: 'Closed', value: 'closed' },
    ];

    // Debounce search
    useEffect(() => {
        if (searchQuery === '' || searchQuery.length >= 3) {
            const timer = setTimeout(() => {
                setDebouncedSearch(searchQuery);
                setFirst(0);
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [searchQuery]);

    // Fetch leads when modal opens or filters change
    useEffect(() => {
        if (visible) {
            fetchLeads();
        }
    }, [visible, userId, positionId, accessType, thisPositionOnly, includeNetwork, first, rows, sortField, sortOrder, debouncedSearch]);

    // Reset state when modal opens/closes and initialize toggles based on leadType
    useEffect(() => {
        if (visible) {
            if (accessType === 'orgposition') {
                // For orgposition: set thisPositionOnly based on leadType
                if (leadType === 'my') {
                    // My Leads: show only this position's own leads
                    setThisPositionOnly(true);
                } else if (leadType === 'network') {
                    // Network Leads: show whole branch leads
                    setThisPositionOnly(false);
                } else {
                    // Total Leads: use filterType prop
                    setThisPositionOnly(filterType === 'this_position_only');
                }
                setIncludeNetwork(true);
            } else {
                // For usermlm/orguser: set includeNetwork based on leadType
                if (leadType === 'my') {
                    // My Leads: show only user's own leads (exclude network)
                    setIncludeNetwork(false);
                } else {
                    // Network or Total Leads: include network leads
                    setIncludeNetwork(true);
                }
            }

            // Reset filters when opening
            setFirst(0);
            setSelectedProductType(null);
            setSelectedStatus(null);
            setSearchFromDate(null);
            setSearchToDate(null);
            setSearchQuery('');
            setDebouncedSearch('');
        } else {
            // Clear data when closing
            setLeads([]);
        }
    }, [visible, leadType, accessType, filterType]);

    const fetchLeads = async () => {
        setLoading(true);
        try {
            // Build request body for unified tree leads API
            const requestBody = {
                access_type: accessType,
                page: Math.floor(first / rows) + 1,
                per_page: rows,
                include_network: includeNetwork
            };

            // Set target_id based on access type
            if (accessType === 'orgposition') {
                if (positionId !== null && positionId !== undefined) {
                    requestBody.target_id = parseInt(positionId, 10);
                }
                requestBody.filter_type = thisPositionOnly ? 'this_position_only' : 'whole_branch';
            } else {
                if (userId !== null && userId !== undefined) {
                    requestBody.target_id = parseInt(userId, 10);
                }
            }

            // Add sorting
            if (sortField) {
                requestBody.sort_field = sortField;
                requestBody.sort_order = sortOrder === 1 ? 'asc' : 'desc';
            }

            // Add filters
            if (debouncedSearch && debouncedSearch.length >= 3) {
                requestBody.search = debouncedSearch;
            }

            if (selectedProductType && selectedProductType !== '') {
                requestBody.product_type = selectedProductType;
            }

            if (selectedStatus && selectedStatus !== '') {
                requestBody.status = selectedStatus;
            }

            if (searchFromDate) {
                requestBody.date_from = searchFromDate.toISOString().split('T')[0];
            }

            if (searchToDate) {
                requestBody.date_to = searchToDate.toISOString().split('T')[0];
            }

            console.log('Fetching tree leads with body:', requestBody);

            // POST request to unified tree leads API
            const response = await axiosInstance.post('/tree/leads', requestBody);

            if (response.data.success) {
                setLeads(response.data.data);
                const pagination = response.data.pagination;
                if (pagination) {
                    setTotalRecords(pagination.total);
                } else {
                    setTotalRecords(response.data.data.length);
                }
            } else {
                toast.error('Failed to fetch leads');
            }
        } catch (error) {
            console.error('Error fetching tree leads:', error);
            if (error.response?.status === 403) {
                toast.error('Access denied: User is not in your network');
            } else if (error.response?.status === 404) {
                toast.error('User not found');
            } else if (error.response?.status === 400) {
                toast.error(error.response?.data?.message || 'Invalid request');
            } else {
                toast.error('Error fetching leads');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setFirst(0);
        fetchLeads();
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setDebouncedSearch('');
        setSearchFromDate(null);
        setSearchToDate(null);
        setSelectedProductType(null);
        setSelectedStatus(null);
        setSortField('created_at');
        setSortOrder(-1);
        setFirst(0);
        if (accessType === 'orgposition') {
            setThisPositionOnly(filterType === 'this_position_only');
        }
        setIncludeNetwork(true);
    };

    const handleViewLead = (lead) => {
        // Open lead details in overlay dialog (preserves leads modal state)
        setSelectedLeadId(lead.id);
        setShowLeadDetails(true);
    };

    // Column templates
    const serialNumberTemplate = (rowData) => {
        return <span className="font-medium">{rowData.sl_no}</span>;
    };

    const leadIdTemplate = (rowData) => {
        return <span className="font-semibold text-primary">#{rowData.id}</span>;
    };

    const nameBodyTemplate = (rowData) => {
        return (
            <div className="flex flex-column">
                <span className="font-semibold">{rowData.full_name}</span>
                {rowData.phone && (
                    <span className="text-sm text-600">{rowData.phone}</span>
                )}
            </div>
        );
    };

    const productTypeBodyTemplate = (rowData) => {
        return <span className="text-sm">{rowData.product_type_display || 'Not Specified'}</span>;
    };

    const loanAmountBodyTemplate = (rowData) => {
        const loanAmount = rowData.loan_amount_required || rowData.loan_amount;
        if (!loanAmount) {
            return <span className="text-500">Not specified</span>;
        }
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(loanAmount);
    };

    const leadStatusBodyTemplate = (rowData) => {
        const c = getProcessingStatusConfig(rowData.lead_status);
        return <Tag value={c.label} severity={c.severity} icon={`pi ${c.icon}`} className="text-xs" />;
    };

    const completionBodyTemplate = (rowData) => {
        const completion = rowData.completion;
        if (!completion) {
            return <span className="text-500">-</span>;
        }

        const percentage = completion.percentage || 0;
        const status = completion.status || 'Unknown';
        const statusColor = completion.status_color || 'secondary';

        const getProgressColor = () => {
            if (percentage === 100) return '#22c55e';
            if (percentage >= 50) return '#f59e0b';
            return '#ef4444';
        };

        return (
            <div className="flex flex-column gap-1">
                <Tag value={status} severity={statusColor} className="text-xs" />
                <ProgressBar
                    value={percentage}
                    showValue={false}
                    className="h-1rem"
                    color={getProgressColor()}
                 aria-label="Progress" />
                <div className="flex justify-content-between">
                    <small className="text-700 font-bold">{percentage}%</small>
                    {(completion.missing_fields_count > 0 || completion.missing_documents_count > 0) && (
                        <small className="text-500">
                            {completion.missing_fields_count > 0 && `${completion.missing_fields_count}F`}
                            {completion.missing_fields_count > 0 && completion.missing_documents_count > 0 && '/'}
                            {completion.missing_documents_count > 0 && `${completion.missing_documents_count}D`}
                        </small>
                    )}
                </div>
            </div>
        );
    };

    const dateBodyTemplate = (rowData) => {
        return (
            <span className="text-sm">
                {new Date(rowData.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                })}
            </span>
        );
    };

    const sharedStatusTemplate = (rowData) => {
        return rowData.is_shared ? (
            <div className="flex align-items-center gap-1">
                <Tag value="Shared" severity="success" className="text-xs" />
                <i className="pi pi-share-alt text-green-500 text-sm"></i>
            </div>
        ) : (
            <Tag value="Private" severity="secondary" className="text-xs" />
        );
    };

    const actionTemplate = (rowData) => {
        return (
            <div className="flex gap-2 justify-content-center">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text p-button-info"
                    tooltip="View Lead Details"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => handleViewLead(rowData)}
                />
            </div>
        );
    };

    // Dialog header
    const dialogHeader = (
        <div className="flex align-items-center gap-3">
            <i className="pi pi-id-card text-2xl" style={{ color: '#EC4899' }}></i>
            <div>
                <div className="text-xl font-bold">Leads - {userName}</div>
                <div className="text-sm text-600 font-normal">View leads from tree network</div>
            </div>
        </div>
    );

    // Dialog footer
    const dialogFooter = (
        <div className="flex justify-content-end">
            <Button label="Close" icon="pi pi-times" onClick={onHide} className="p-button-text" />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header={dialogHeader}
            footer={dialogFooter}
            style={{ width: '95vw', maxHeight: '90vh' }}
            maximizable
            modal
            className="mlm-user-leads-modal"
        >
            {/* Filters Section */}
            <div className="surface-100 border-round p-3 mb-3">
                {/* Desktop Layout - Original horizontal layout */}
                <div className="hidden md:block">
                    <div className="flex flex-wrap align-items-center gap-3">
                        {/* Network Toggle */}
                        {(accessType === 'usermlm' || accessType === 'orguser') && (
                            <div className="flex align-items-center gap-2">
                                <label htmlFor="leadsNetworkToggle" className="text-sm font-semibold text-700 white-space-nowrap">
                                    Include Network
                                </label>
                                <InputSwitch
                                    inputId="leadsNetworkToggle"
                                    checked={includeNetwork}
                                    onChange={(e) => {
                                        setIncludeNetwork(e.value);
                                        setFirst(0);
                                    }}
                                    tooltip={includeNetwork ? "Showing leads from user + network" : "Showing only user's leads"}
                                    tooltipOptions={{ position: 'bottom' }}
                                />
                            </div>
                        )}

                        {/* Position Filter Toggle */}
                        {accessType === 'orgposition' && (
                            <div className="flex align-items-center gap-2">
                                <label htmlFor="leadsPositionFilterToggle" className="text-sm font-semibold text-700 white-space-nowrap">
                                    This Position Only
                                </label>
                                <InputSwitch
                                    inputId="leadsPositionFilterToggle"
                                    checked={thisPositionOnly}
                                    onChange={(e) => {
                                        setThisPositionOnly(e.value);
                                        setFirst(0);
                                    }}
                                    tooltip={thisPositionOnly ? "Showing leads from this position only" : "Showing leads from whole branch"}
                                    tooltipOptions={{ position: 'bottom' }}
                                />
                            </div>
                        )}

                        {/* Search */}
                        <IconField iconPosition="left" className="flex-grow-1">
                            <InputIcon className="pi pi-search" />
                            <InputText
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Search (min 3 chars)"
                                className="w-full"
                            />
                        </IconField>

                        {/* From Date */}
                        <Calendar
                            value={searchFromDate}
                            onChange={(e) => setSearchFromDate(e.value)}
                            dateFormat="dd-mm-yy"
                            placeholder="From Date"
                            showIcon
                            style={{ width: '160px' }}
                        />

                        {/* To Date */}
                        <Calendar
                            value={searchToDate}
                            onChange={(e) => setSearchToDate(e.value)}
                            dateFormat="dd-mm-yy"
                            placeholder="To Date"
                            showIcon
                            style={{ width: '160px' }}
                        />

                        {/* Product Type */}
                        <Dropdown
                            value={selectedProductType}
                            options={productTypeOptions}
                            onChange={(e) => setSelectedProductType(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="All Products"
                            showClear
                            style={{ width: '180px' }}
                        />

                        {/* Status */}
                        <Dropdown
                            value={selectedStatus}
                            options={statusOptions}
                            onChange={(e) => setSelectedStatus(e.value)}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="All Status"
                            showClear
                            style={{ width: '150px' }}
                        />

                        {/* Action Buttons */}
                        <Button
                            icon="pi pi-filter-slash"
                            label="Clear"
                            onClick={handleClearFilters}
                            className="p-button-outlined p-button-secondary"
                        />
                        <Button
                            icon="pi pi-search"
                            label="Search"
                            onClick={handleSearch}
                            className="p-button-primary"
                        />
                    </div>
                </div>

                {/* Mobile Layout - Stacked with toggle at bottom */}
                <div className="block md:hidden">
                    <div className="grid">
                        {/* Row 1: Search */}
                        <div className="col-12">
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search (min 3 chars)"
                                    className="w-full"
                                />
                            </IconField>
                        </div>

                        {/* Row 2: Date Filters - 2 per row */}
                        <div className="col-6">
                            <label className="block text-sm font-medium text-600 mb-1">From Date</label>
                            <Calendar
                                value={searchFromDate}
                                onChange={(e) => setSearchFromDate(e.value)}
                                dateFormat="dd-mm-yy"
                                placeholder="Select date"
                                showIcon
                                className="w-full"
                            />
                        </div>
                        <div className="col-6">
                            <label className="block text-sm font-medium text-600 mb-1">To Date</label>
                            <Calendar
                                value={searchToDate}
                                onChange={(e) => setSearchToDate(e.value)}
                                dateFormat="dd-mm-yy"
                                placeholder="Select date"
                                showIcon
                                className="w-full"
                            />
                        </div>

                        {/* Row 3: Dropdown Filters - 2 per row */}
                        <div className="col-6">
                            <label className="block text-sm font-medium text-600 mb-1">Product Type</label>
                            <Dropdown
                                value={selectedProductType}
                                options={productTypeOptions}
                                onChange={(e) => setSelectedProductType(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="All Products"
                                showClear
                                className="w-full"
                            />
                        </div>
                        <div className="col-6">
                            <label className="block text-sm font-medium text-600 mb-1">Status</label>
                            <Dropdown
                                value={selectedStatus}
                                options={statusOptions}
                                onChange={(e) => setSelectedStatus(e.value)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="All Status"
                                showClear
                                className="w-full"
                            />
                        </div>

                        {/* Row 4: Toggle + Action Buttons */}
                        <div className="col-12 flex justify-content-between align-items-center gap-2 mt-2">
                            {/* Mobile Toggle */}
                            {(accessType === 'usermlm' || accessType === 'orguser') && (
                                <div className="flex align-items-center gap-2">
                                    <label htmlFor="leadsNetworkToggleMobile" className="text-xs font-semibold text-700 white-space-nowrap">
                                        Network
                                    </label>
                                    <InputSwitch
                                        inputId="leadsNetworkToggleMobile"
                                        checked={includeNetwork}
                                        onChange={(e) => {
                                            setIncludeNetwork(e.value);
                                            setFirst(0);
                                        }}
                                    />
                                </div>
                            )}
                            {accessType === 'orgposition' && (
                                <div className="flex align-items-center gap-2">
                                    <label htmlFor="leadsPositionToggleMobile" className="text-xs font-semibold text-700 white-space-nowrap">
                                        Position Only
                                    </label>
                                    <InputSwitch
                                        inputId="leadsPositionToggleMobile"
                                        checked={thisPositionOnly}
                                        onChange={(e) => {
                                            setThisPositionOnly(e.value);
                                            setFirst(0);
                                        }}
                                    />
                                </div>
                            )}
                            {/* Spacer if no toggle */}
                            {accessType !== 'usermlm' && accessType !== 'orguser' && accessType !== 'orgposition' && <div></div>}

                            {/* Mobile Action Buttons */}
                            <div className="flex gap-2">
                                <Button
                                    icon="pi pi-filter-slash"
                                    label="Clear"
                                    onClick={handleClearFilters}
                                    className="p-button-outlined p-button-secondary p-button-sm"
                                />
                                <Button
                                    icon="pi pi-search"
                                    label="Search"
                                    onClick={handleSearch}
                                    className="p-button-primary p-button-sm"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Leads DataTable */}
            <Card>
                <DataTable
                    value={leads}
                    lazy
                    paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPage={(e) => {
                        setFirst(e.first);
                        setRows(e.rows);
                    }}
                    onSort={(e) => {
                        setSortField(e.sortField);
                        setSortOrder(e.sortOrder);
                        setFirst(0);
                    }}
                    sortField={sortField}
                    sortOrder={sortOrder}
                    sortMode="single"
                    removableSort
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords} leads"
                    rowsPerPageOptions={[10, 20, 50]}
                    dataKey="id"
                    loading={loading}
                    emptyMessage="No leads found"
                    scrollable
                    scrollHeight="400px"
                    stripedRows
                    size="small"
                    showGridlines
                >
                    <Column
                        field="sl_no"
                        header="Sl."
                        body={serialNumberTemplate}
                        style={{ minWidth: '60px', textAlign: 'center' }}
                        bodyStyle={{ textAlign: 'center' }}
                    />
                    <Column
                        field="id"
                        header="Lead ID"
                        body={leadIdTemplate}
                        sortable
                        style={{ minWidth: '90px' }}
                    />
                    <Column
                        field="full_name"
                        header="Name & Phone"
                        body={nameBodyTemplate}
                        sortable
                        style={{ minWidth: '180px' }}
                    />
                    <Column
                        field="pan"
                        header="PAN"
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="product_type"
                        header="Product"
                        body={productTypeBodyTemplate}
                        sortable
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="loan_amount_required"
                        header="Loan Amt"
                        body={loanAmountBodyTemplate}
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="lead_status"
                        header="Status"
                        body={leadStatusBodyTemplate}
                        sortable
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        header="Progress"
                        body={completionBodyTemplate}
                        style={{ minWidth: '130px' }}
                    />
                    <Column
                        field="created_at"
                        header="Created"
                        body={dateBodyTemplate}
                        sortable
                        style={{ minWidth: '100px' }}
                    />
                    <Column
                        field="is_shared"
                        header="Shared"
                        body={sharedStatusTemplate}
                        style={{ minWidth: '90px' }}
                    />
                    <Column
                        header="Actions"
                        body={actionTemplate}
                        exportable={false}
                        alignHeader="center"
                        bodyClassName="text-center"
                        style={{ minWidth: '80px' }}
                    />
                </DataTable>
            </Card>

            {/* Lead Details Dialog - Opens on top, preserves leads modal state */}
            <LeadDetailsDialog
                visible={showLeadDetails}
                onHide={() => {
                    setShowLeadDetails(false);
                    setSelectedLeadId(null);
                }}
                leadId={selectedLeadId}
                treeType={accessType}
            />
        </Dialog>
    );
};

export default MLMUserLeadsModal;
